/**
 * Branch Utility Functions
 * Helper functions for branch management UI state
 */

import {
  Branch,
  BranchManagementState,
  BranchStatistics,
} from "../types/branch.types";

/**
 * Reset branch management state to initial values
 */
export const resetBranchState = (): BranchManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  isStatisticsModalOpen: false,
  editingBranch: null,
  deletingBranch: null,
  selectedBranchStatistics: null,
});

/**
 * Open drawer for editing a branch
 */
export const openEditDrawer = (branch: Branch): BranchManagementState => ({
  isDrawerOpen: true,
  isDeleteModalOpen: false,
  isStatisticsModalOpen: false,
  editingBranch: branch,
  deletingBranch: null,
  selectedBranchStatistics: null,
});

/**
 * Open drawer for creating a new branch
 */
export const openCreateDrawer = (): BranchManagementState => ({
  isDrawerOpen: true,
  isDeleteModalOpen: false,
  isStatisticsModalOpen: false,
  editingBranch: null,
  deletingBranch: null,
  selectedBranchStatistics: null,
});

/**
 * Close the drawer
 */
export const closeDrawer = (): BranchManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  isStatisticsModalOpen: false,
  editingBranch: null,
  deletingBranch: null,
  selectedBranchStatistics: null,
});

/**
 * Open delete confirmation modal
 */
export const openDeleteModal = (branch: Branch): BranchManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: true,
  isStatisticsModalOpen: false,
  editingBranch: null,
  deletingBranch: branch,
  selectedBranchStatistics: null,
});

/**
 * Close delete confirmation modal
 */
export const closeDeleteModal = (): BranchManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  isStatisticsModalOpen: false,
  editingBranch: null,
  deletingBranch: null,
  selectedBranchStatistics: null,
});

/**
 * Open statistics modal
 */
export const openStatisticsModal = (
  branch: Branch,
  statistics: BranchStatistics
): BranchManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  isStatisticsModalOpen: true,
  editingBranch: branch,
  deletingBranch: null,
  selectedBranchStatistics: statistics,
});

/**
 * Close statistics modal
 */
export const closeStatisticsModal = (): BranchManagementState => ({
  isDrawerOpen: false,
  isDeleteModalOpen: false,
  isStatisticsModalOpen: false,
  editingBranch: null,
  deletingBranch: null,
  selectedBranchStatistics: null,
});

/**
 * Get drawer title based on whether editing or creating
 */
export const getDrawerTitle = (editingBranch: Branch | null): string => {
  return editingBranch ? "Редактировать филиал" : "Добавить филиал";
};

/**
 * Format branch location string
 */
export const formatBranchLocation = (branch: Branch): string => {
  const parts = [branch.city, branch.state, branch.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "Не указано";
};

/**
 * Format branch contact info
 */
export const formatBranchContact = (branch: Branch): string => {
  const contacts = [];
  if (branch.phone) contacts.push(branch.phone);
  if (branch.email) contacts.push(branch.email);
  return contacts.length > 0 ? contacts.join(" • ") : "Нет контактов";
};

/**
 * Get branch status badge class
 */
export const getBranchStatusClass = (isActive: boolean): string => {
  return isActive ? "badge--success" : "badge--error";
};

/**
 * Get branch status text
 */
export const getBranchStatusText = (isActive: boolean): string => {
  return isActive ? "Активен" : "Неактивен";
};

/**
 * Validate branch form data
 */
export const validateBranchData = (data: Partial<Branch>): string[] => {
  const errors: string[] = [];

  if (!data.name || data.name.trim().length === 0) {
    errors.push("Название филиала обязательно");
  }

  if (data.email && !isValidEmail(data.email)) {
    errors.push("Неверный формат email");
  }

  if (
    data.latitude !== undefined &&
    (data.latitude < -90 || data.latitude > 90)
  ) {
    errors.push("Широта должна быть от -90 до 90");
  }

  if (
    data.longitude !== undefined &&
    (data.longitude < -180 || data.longitude > 180)
  ) {
    errors.push("Долгота должна быть от -180 до 180");
  }

  return errors;
};

/**
 * Validate email format
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
